/* eslint-disable @next/next/no-img-element */
import * as React from "react";
import {
    Box,
    Dialog,
    DialogActions,
    Typography,
    IconButton,
    TextField,
    Grid,
    Modal,
    FormLabel, Autocomplete,
    MenuItem,
    FormControl,
    FormHelperText,
    Select,
    CircularProgress,
    Card,
    Stack,
    Skeleton
} from "@mui/material";
import CtaButton from "../buttonWithArrow"; // or replace with your own CTA button
import Heading from "../heading";
import Image from "next/image";
import PricingCard from "./PricingCard";
import CloseIcon from '@mui/icons-material/Close'
import PaymentModes from "../../components/PaymentModesForHourly";
import ErrorPage from "../../components/PaymentModesForHourly/error_page";
import CancelIcon from '@mui/icons-material/Cancel'
import { countries } from '../../data/countries';

const MAX_W = 1300;     // section content cap
const IMG_W = 1200;      // max width for the illustration


const pillInputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 999,
        bgcolor: "#fff",
    },
};


function FeatureRowSkeleton() {
    return (
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <Skeleton variant="circular" width={16} height={16} sx={{ bgcolor: "rgba(255,255,255,.18)" }} />
            <Skeleton variant="text" width="80%" height={18} sx={{ bgcolor: "rgba(255,255,255,.18)" }} />
        </Stack>
    );
}

function PricingCardSkeleton() {
    return (
        <Card
            elevation={8}
            sx={{
                borderRadius: 4,
                overflow: "hidden",
                // p: 3,
                height: "100%",
                bgcolor: "transparent",
            }}
        >
            <Box
                sx={{
                    height: "100%",
                    borderRadius: 4,
                    p: { xs: 3, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    gap: 2.5,
                    // teal-to-black gradient background like the screenshot
                    background:
                        "radial-gradient(1200px 600px at -10% -10%, rgba(0,158,150,.35) 0%, rgba(0,0,0,.25) 40%, rgba(0,0,0,.85) 100%), linear-gradient(180deg, #0c3b3f 0%, #0a0a0a 100%)",
                }}
            >
                {/* Top: icon + title */}
                <Stack spacing={2.5} alignItems="flex-start">
                    <Skeleton variant="circular" width={56} height={56} sx={{ bgcolor: "rgba(255,255,255,.18)" }} />
                    <Skeleton variant="text" width={220} height={28} sx={{ bgcolor: "rgba(255,255,255,.18)" }} />
                </Stack>

                {/* Price block */}
                <Stack spacing={1}>
                    <Skeleton variant="text" width={90} height={64} sx={{ bgcolor: "rgba(255,255,255,.18)" }} />
                    <Skeleton variant="text" width={170} height={22} sx={{ bgcolor: "rgba(255,255,255,.18)" }} />
                    <Skeleton variant="text" width={140} height={18} sx={{ bgcolor: "rgba(255,255,255,.18)" }} />
                </Stack>

                {/* Features */}
                <Stack spacing={1.25} sx={{ mt: 1 }}>
                    <FeatureRowSkeleton />
                    <FeatureRowSkeleton />
                    <FeatureRowSkeleton />
                    <FeatureRowSkeleton />
                    <FeatureRowSkeleton />
                </Stack>

                {/* CTA */}
                <Box sx={{ pt: 1 }}>
                    <Skeleton
                        variant="rounded"
                        height={52}
                        sx={{
                            width: { xs: "100%", sm: 280 },
                            borderRadius: 999,
                            mx: { xs: 0, sm: "auto" },
                            bgcolor: "rgba(255,255,255,.18)",
                        }}
                    />
                </Box>
            </Box>
        </Card>
    );
}

const style = {
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        backgroundColor: "rgba(0,0,0,0.6)", // dimmed background
    },
    container: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        px: { xs: 2, sm: 3 },
        py: 2,
        borderBottom: "1px solid #eee",
        bgcolor: "#fff",
    },
    header: {
        fontWeight: 700,
        fontSize: 24,
        mb: 2
    },
    closeBtn: { position: "absolute", top: 8, right: 8, width: 36, height: 36 },
    input: {
        width: '100%',
        mb: 2,
        backgroundColor: '#f3f3f3',
        borderRadius: '12px',
        input: {
            padding: '22px 14px'
        },
        '& .MuiFilledInput-root': {
            backgroundColor: '#f3f3f3',
            borderRadius: '12px',
            boxShadow: 'none',
            '&:before': {
                borderBottom: '0 !important' // removes the bottom underline
            },
            '&:after': {
                borderBottom: '0 !important' // removes the focus underline
            },
            '&:hover:before': {
                borderBottom: '0 !important'
            }
        },
        '& .MuiFormHelperText-root': {
            marginLeft: 0
        }
    },
    select: {
        width: '100%',
        mb: 2,
        backgroundColor: '#f3f3f3',
        borderRadius: '12px',

        '& .MuiFilledInput-root': {
            backgroundColor: '#f3f3f3',
            borderRadius: '12px',
            paddingTop: '12px',
            paddingBottom: '12px',
            '&:before, &:after, &:hover:before': {
                borderBottom: '0 !important'
            }
        },
        '& .MuiSelect-filled': {
            padding: '12px 14px',
            backgroundColor: '#f3f3f3'
        }
    },
    submitBtn: {
        background: 'linear-gradient(to right, #9CD0C9, #32788E)',
        color: '#fff',
        fontWeight: 600,
        borderRadius: '12px',
        fontSize: '16px',
        textTransform: 'none',
        mt: 2,
        py: 1.2
    },
    fileInputBox: {
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        // bgcolor: "#f3f3f3",
        borderRadius: 999,
        px: 2,
        py: 1.5,
        mb: 2,
        border: "1px solid #c4c4c4",
    },
    chooseBtn: {
        background: "linear-gradient(to right, #9CD0C9, #32788E)",
        color: "#fff",
        fontWeight: 600,
        borderRadius: "10px",
        fontSize: 13,
        px: 2,
        py: 0.75,
        textTransform: "none",
        "&:hover": {
            opacity: 0.95,
        },
    },
    fileRemoveBtn: {
        color: "#7bcbc4",
        "&:hover": { color: "#5fbab2" },
    },
}

export default function RoiInvestorBenefits({ loader, data, handleOpenEnquire }) {
    // console.log("OurPackages data:", data);
    const { title = 'Our Packages', subtitle = 'Looking for something?', button, image, showSparkle, bgcolor, width, pricingData } = data;

    // 🔹 Payment modal state (added)
    const [paymentOpen, setPaymentOpen] = React.useState(false);
    const [paymentMode, setPaymentMode] = React.useState("1");
    const [transRef, setTransRef] = React.useState(null);
    const [errorMessage, setErrorMessage] = React.useState(null);
    const [selectedPlan, setSelectedPlan] = React.useState(null);


    return (
        <Box
            component="section"
            sx={{
                py: { xs: 8, md: 4 },
                px: { xs: 2, sm: 3 },
                // bgcolor: bgcolor ?? "#f7f9f9",
                maxWidth: "1320px", mx: "auto"
            }}
        >
            {/* Header */}
            <Heading title={title} subtitle={subtitle} lineClamp={showSparkle ? 2 : 1} showSparkle={showSparkle ? "/img/investments/Sparkle.svg" : undefined} mb={7} />
            {/* 👇 avoid duplicating #fleet id elsewhere */}
            {
                loader ? <Box sx={{ px: { xs: 1, md: 4 }, py: { xs: 3, md: 6 } }}>
                    <Grid container spacing={{ xs: 2, md: 4 }}>
                        {[0, 1, 2].map((i) => (
                            <Grid key={i} item xs={12} md={4}>
                                <PricingCardSkeleton />
                            </Grid>
                        ))}
                    </Grid>
                </Box> :
                    <Box id="pricing" sx={{ scrollMarginTop: { xs: '80px', md: '96px' } }}>
                        <Box
                            sx={{
                                display: { xs: 'flex', md: 'grid' },
                                gridTemplateColumns: { md: 'repeat(3, 1fr)' },
                                gap: { xs: 2, md: 3 },
                                px: { xs: 2, md: 0 },
                                alignItems: 'stretch',        // ⬅ stretch children same height

                                // Mobile horizontal scroll
                                overflowX: { xs: 'auto', md: 'visible' },
                                scrollSnapType: { xs: 'x mandatory', md: 'none' },
                                WebkitOverflowScrolling: 'touch',
                                '&::-webkit-scrollbar': { display: 'none' },
                                msOverflowStyle: 'none',
                                scrollbarWidth: 'none',

                                // Card sizing in mobile track
                                '& > .card-item': {
                                    flex: { xs: '0 0 85%', sm: '0 0 60%' }, // visible width per snap
                                    scrollSnapAlign: 'center',
                                },
                            }}
                        >
                            {data?.map((data, inx) => (
                                <Box key={inx} className="card-item">
                                    <PricingCard
                                        plan={data.title}
                                        subtitle={data?.subtitle}
                                        actionType={data?.actionType}
                                        currency={data?.currency}
                                        price={data.priceValue}
                                        per={data.durationTypeFormated}
                                        billingNote={data.billingCycle}
                                        features={data.features}
                                        icon={data.icon}
                                        ctaText={data.buttonName}
                                        hasActiveSubscription={data?.hasActiveSubscription || false}
                                        // highlighted={inx === 1}
                                        validityTo={data?.subscriptionValidity?.validityTo}
                                        onClick={() => handleOpenEnquire(data)}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
            }


            {/* ✅ Payment Dialog */}
            {paymentOpen && (
                <Dialog
                    fullWidth
                    maxWidth="sm"
                    open={paymentOpen}
                    keepMounted
                    onClose={handleCancelPayment}
                >
                    <DialogActions
                        className="mt-5 mb-5 items-center justify-between d-flex"
                    >
                        {errorMessage && errorMessage?.status !== 1 ? null : (
                            <Typography
                                variant="h6"
                                className="fw-bold"
                                sx={{
                                    fontSize: {
                                        xs: "16px",
                                        sm: "18px",
                                        md: "20px",
                                        lg: "24px",
                                    },
                                    width: "100%",
                                    textAlign: "center",
                                }}
                            >
                                How would you like to pay?
                            </Typography>
                        )}
                    </DialogActions>

                    {errorMessage && errorMessage?.status !== 1 ? null : <hr />}

                    {errorMessage && errorMessage?.status !== 1 ? (
                        <ErrorPage
                            handleClose={() => window.location.reload()}
                            errorMessage={errorMessage}
                        />
                    ) : (
                        <PaymentModes
                            open={paymentOpen}
                            setPaymentMode={setPaymentMode}
                            setTransRef={setTransRef}
                            paymentMode={paymentMode}
                            price={selectedPlan?.priceValue || 0}
                            handleClose={handleCancelPayment}
                            errorMessage={errorMessage}
                            setErrorMessage={setErrorMessage}
                            paymentBottom={true}
                        />
                    )}
                </Dialog>
            )}




        </Box>
    );
}

